import { API_BASE_URL } from 'lib/config/apiEndpoints';
import type { ShippingEstimateObject } from 'components/ring-air/utils/types';

/** India 6-digit pincode; same shape as Blood Vision checkout. */
export const IN_PINCODE_RE = /^[1-9][0-9]{5}$/;

export type SddDeliveryMode = 'same_day' | 'two_hour' | 'next_day';

export type SddServiceabilityData = {
  eligible: boolean;
  pincode: string;
  city?: string;
  mode?: SddDeliveryMode;
  cutoff_ist?: string;
  eta_date?: string;
  /** Latest arrival instant, e.g. 2026-09-02T15:00:00+05:30. Display-only. */
  delivery_by?: string;
  /** Order-by instant, e.g. 2026-09-03T19:00:00+05:30. Timer deadline. */
  order_by?: string;
  title: string;
  subtitle?: string;
  advisory?: string;
};

type SddServiceabilityErr = {
  status: 'error';
  error?: { code?: string | number; message?: string };
};

export type SddServiceabilityFailure = {
  ok: false;
  code: string;
  message: string;
};

export const isDeliveryMode = (value: unknown): value is SddDeliveryMode =>
  value === 'same_day' || value === 'two_hour' || value === 'next_day';

const asTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const parseData = (raw: unknown): SddServiceabilityData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const title = asTrimmed(d.title);
  const pincode = asTrimmed(d.pincode);
  if (!pincode) return null;
  const eligible = d.eligible === true;
  // Eligible results must carry copy; ineligible may omit title until product lands it.
  if (eligible && !title) return null;
  return {
    eligible,
    pincode,
    title: title ?? '',
    city: asTrimmed(d.city),
    mode: isDeliveryMode(d.mode) ? d.mode : undefined,
    cutoff_ist: asTrimmed(d.cutoff_ist),
    eta_date: asTrimmed(d.eta_date),
    delivery_by: asTrimmed(d.delivery_by),
    order_by: asTrimmed(d.order_by),
    subtitle: asTrimmed(d.subtitle),
    advisory: asTrimmed(d.advisory),
  };
};

/** One value of `ShippingEstimateObject` — a single SKU's warehouse ETA. */
type StandardEtaEstimate = ShippingEstimateObject[string];

export type DeliveryEstimateData = {
  sku: string;
  /** Fresher per-SKU warehouse ETA; null when the backend has nothing newer
   * than the SSR `ring_sku_estimates_v2` map already on the page. */
  standard: StandardEtaEstimate | null;
  /** Same-day/next-day kiosk-stock result; byte-for-byte the old
   * `sdd_serviceability` payload. Null only when the request omitted a
   * pincode. */
  express: SddServiceabilityData | null;
};

type DeliveryEstimateOk = {
  status: 'ok';
  data: {
    sku?: string;
    standard?: unknown;
    express?: unknown;
  };
};

export type DeliveryEstimateResult =
  | { ok: true; data: DeliveryEstimateData }
  | SddServiceabilityFailure;

const parseStandard = (raw: unknown): StandardEtaEstimate | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const date = asTrimmed(d.date);
  const defaultCustomEta = asTrimmed(d.default_custom_eta);
  if (!date && !defaultCustomEta) return null;
  return {
    date: date ?? '',
    default_custom_eta: defaultCustomEta ?? date ?? '',
    mcf_for_eta: d.mcf_for_eta === true,
    show_mcf_logo: d.show_mcf_logo === true,
  };
};

/**
 * Combined kiosk-stock-aware endpoint: one call replaces `sdd_serviceability`
 * (now `data.express`) + the per-SKU default ETA (now `data.standard`).
 * `sku` is mandatory server-side — an empty/invalid sku is a 400, surfaced
 * here as `{ ok: false, code: 'invalid_sku' }` so callers can treat it as
 * "no SKU resolved yet" rather than a network error.
 */
export async function fetchDeliveryEstimate({
  sku,
  pincode,
  signal,
}: {
  sku: string;
  pincode?: string;
  signal?: AbortSignal;
}): Promise<DeliveryEstimateResult> {
  const params = new URLSearchParams({ sku });
  if (pincode) params.set('pincode', pincode);
  const url = `${API_BASE_URL}/api/web_v1/ring/delivery_estimate?${params.toString()}`;
  const response = await fetch(url, { signal });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (response.status === 400) {
    // Production only 400s on a missing/empty sku today, with the generic
    // numeric code 400 (verified by curl: {"code":400,"message":"Select a
    // valid ring colour and size"}) — so unlike the retired pincode-only
    // endpoint, numeric 400 here means a sku problem. Only an explicit
    // 'invalid_pincode' code (if the backend ever adds pincode validation to
    // this endpoint) surfaces as the inline pincode field error.
    const err = (body ?? {}) as SddServiceabilityErr;
    const rawCode = err.error?.code;
    const isPincodeError = rawCode === 'invalid_pincode';
    return {
      ok: false,
      code: isPincodeError ? 'invalid_pincode' : 'invalid_sku',
      message: asTrimmed(err.error?.message) ?? '',
    };
  }

  if (response.status === 404) {
    // Kept as its own code purely so a 404 is distinguishable from a 5xx in
    // GA4. It no longer unlocks any fallback: the old pincode-only
    // sdd_serviceability path was removed once both staging and production
    // started answering 200 here (production returns eligible:false even for
    // an unknown SKU), so a 404 now means something is genuinely wrong and is
    // handled like any other non-2xx — warehouse date, never a same-day
    // promise sourced from an endpoint that cannot see the SKU.
    return { ok: false, code: 'not_found', message: '' };
  }

  if (!response.ok) {
    return { ok: false, code: 'http_error', message: '' };
  }

  const parsed = body as DeliveryEstimateOk | null;
  if (parsed?.status !== 'ok' || !parsed.data) {
    return { ok: false, code: 'bad_response', message: '' };
  }

  if (pincode && parsed.data.express === null) {
    // Contractually `express: null` means "no pincode was sent" — getting it
    // back explicitly null WITH a pincode is a server-side anomaly, not an
    // unparseable payload. Treat it the same quiet way as an invalid sku
    // (editing phase, no error card) rather than the bad_response fallback.
    return { ok: false, code: 'invalid_sku', message: '' };
  }

  return {
    ok: true,
    data: {
      sku: asTrimmed(parsed.data.sku) ?? sku,
      standard: parseStandard(parsed.data.standard),
      express: parseData(parsed.data.express),
    },
  };
}
